import { type DragEvent, type MouseEvent, type PointerEvent, useRef, useState } from "react";

// A drag of the mouse moving a row sideways, as a swipe moves it on a phone (a phone's touch scrolls the row
// itself): a card's photo strip (Photos.tsx) and its jump row (CardSections.tsx). A press becomes a drag once it has
// moved a few pixels; the click that ends a drag does nothing, so letting go never opens or jumps to what's under
// the mouse, while a click that didn't move still does. A drag picks nothing up (no text, no image).
const DRAG_FROM = 6;

export function useMouseDrag<T extends HTMLElement>({
  enabled = true,
  onPress,
  onDrop,
}: {
  enabled?: boolean;
  // A press with the mouse begins: what the row needs to know of where it was (the strip, the photo showing).
  onPress?: (el: T) => void;
  // Let go after a drag, `dx` pixels from where it was pressed: `done` once the row has settled (the strip eases onto
  // a photo first); with none given, it's settled at once.
  onDrop?: (el: T, dx: number, done: () => void) => void;
} = {}) {
  const [dragging, setDragging] = useState(false);
  const press = useRef<{ pointer: number; x: number; left: number; moved: boolean } | null>(null);
  const dragged = useRef(false);
  const handlers = {
    onPointerDown: (e: PointerEvent<T>) => {
      dragged.current = false;
      if (!enabled || e.pointerType !== "mouse" || e.button !== 0) return;
      press.current = { pointer: e.pointerId, x: e.clientX, left: e.currentTarget.scrollLeft, moved: false };
      onPress?.(e.currentTarget);
    },
    onPointerMove: (e: PointerEvent<T>) => {
      const p = press.current;
      if (!p || p.pointer !== e.pointerId) return;
      const dx = e.clientX - p.x;
      if (!p.moved) {
        if (Math.abs(dx) < DRAG_FROM) return;
        p.moved = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        setDragging(true);
      }
      e.currentTarget.scrollLeft = p.left - dx;
    },
    onPointerUp: (e: PointerEvent<T>) => end(e),
    onPointerCancel: (e: PointerEvent<T>) => end(e),
    onClickCapture: (e: MouseEvent<T>) => {
      if (!dragged.current) return;
      dragged.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
    onDragStart: (e: DragEvent<T>) => e.preventDefault(),
  };
  const end = (e: PointerEvent<T>) => {
    const p = press.current;
    if (!p || p.pointer !== e.pointerId) return;
    press.current = null;
    if (!p.moved) return;
    // The click this release makes does nothing; one that never comes doesn't block the next.
    dragged.current = e.type === "pointerup";
    setTimeout(() => {
      dragged.current = false;
    });
    const done = () => setDragging(false);
    if (onDrop) onDrop(e.currentTarget, e.clientX - p.x, done);
    else done();
  };
  // Whether a drag is moving the row right now (its scrolling is the drag's, not someone scrolling it).
  const moving = () => press.current?.moved ?? false;
  return { dragging, moving, handlers };
}
